import { Link, useParams } from "react-router-dom";
import { Check, ArrowRight, MessageCircle } from "lucide-react";
import Seo, { crumbsLd, faqLd } from "@/components/site/Seo";
import { PageHero, FaqList, SectionHead } from "@/components/site/Blocks";
import PricingCards from "@/components/site/PricingCards";
import { CtaBand } from "@/components/site/Cta";
import { getSector, SECTORS } from "@/data/sectors";
import { waLink } from "@/data/site";
import NotFound from "./NotFound";

export default function SectorPage() {
  const { slug } = useParams();
  const s = getSector(slug);
  if (!s) return <NotFound />;
  const crumbs = [{ label: "Home", to: "/" }, { label: "Soluzioni", to: "/soluzioni" }, { label: s.name, to: `/soluzioni/${s.slug}` }];
  return (
    <>
      <Seo path={`/soluzioni/${s.slug}`} title={s.title} description={s.description} jsonLd={[crumbsLd(crumbs), faqLd(s.faqs)]} />
      <PageHero crumbs={crumbs} eyebrow={`Soluzioni · ${s.name}`} title={s.h1} intro={s.intro}>
        <Link to="/preventivo-gratuito" data-testid="sector-quote-btn" className="btn-primary">Richiedi un preventivo <ArrowRight className="h-4 w-4" /></Link>
        <a href={waLink("siti")} target="_blank" rel="noopener noreferrer" data-testid="sector-whatsapp-btn" className="btn-ghost"><MessageCircle className="h-4 w-4" />WhatsApp</a>
      </PageHero>
      <section className="py-14 md:py-20">
        <div className="wrap grid gap-14 lg:grid-cols-2">
          <div>
            <h2 className="text-2xl font-bold sm:text-3xl">Le esigenze del settore</h2>
            <ul className="mt-6 space-y-3">{s.needs.map((n) => <li key={n} className="flex gap-3 text-[15px]"><Check className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />{n}</li>)}</ul>
            <h2 className="mt-14 text-2xl font-bold sm:text-3xl">Funzionalità più utili</h2>
            <ul className="mt-6 grid gap-3">{s.features.map((n) => <li key={n} className="rounded-xl bg-slate-50 px-4 py-3 text-[15px]">{n}</li>)}</ul>
            <h2 className="mt-14 text-2xl font-bold sm:text-3xl">I vantaggi del sito</h2>
            <p className="mt-4 text-base leading-relaxed text-slate-600 md:text-lg">{s.benefits}</p>
          </div>
          <div className="lg:sticky lg:top-28 lg:self-start">
            <figure className="overflow-hidden rounded-3xl border border-slate-200">
              <img src={s.img} alt={`Immagine di esempio per ${s.name.toLowerCase()}`} loading="lazy" width="800" height="600" className="aspect-[4/3] w-full object-cover" />
              <figcaption className="flex items-center justify-between gap-4 bg-white p-5">
                <div>
                  <p className="font-display font-bold">Demo {s.demo}</p>
                  <p className="text-sm text-slate-600">{s.demoUrl ? "Sito dimostrativo navigabile, attività fittizia." : "Sito dimostrativo in preparazione. Immagine di repertorio."}</p>
                </div>
                {s.demoUrl
                  ? <Link to={s.demoUrl} data-testid="sector-demo-link" className="btn-primary shrink-0 !px-4 !py-2">Apri la demo</Link>
                  : <span data-testid="sector-demo-status" className="shrink-0 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">In arrivo</span>}
              </figcaption>
            </figure>
          </div>
        </div>
      </section>
      <section className="bg-soft py-14 md:py-20">
        <div className="wrap">
          <SectionHead eyebrow="Pacchetti adatti" title={`I pacchetti più scelti per ${s.name.toLowerCase()}`} intro="Prezzi IVA esclusa. Tutti i dettagli nella pagina prezzi." />
          <div className="mt-12"><PricingCards ids={s.packages} /></div>
        </div>
      </section>
      <section className="py-14 md:py-20">
        <div className="wrap grid gap-12 lg:grid-cols-[0.8fr_1.2fr]">
          <SectionHead eyebrow="FAQ" title="Domande frequenti" />
          <FaqList faqs={s.faqs} id="sector-faq" />
        </div>
      </section>
      <section className="border-t border-slate-100 py-10">
        <div className="wrap flex flex-wrap gap-3">
          {SECTORS.filter((x) => x.slug !== s.slug).map((x) => <Link key={x.slug} to={`/soluzioni/${x.slug}`} className="rounded-full border border-slate-200 px-4 py-2 text-sm font-medium hover:border-slate-900">{x.name}</Link>)}
        </div>
      </section>
      <CtaBand />
    </>
  );
}
